import { Button, LoadingIndicator } from "@sunrun/experience-ui-components";
import { useEffect, useState, useMemo } from "react";
import styled from "@emotion/styled/macro";
import { Modal, ErrorModal, tokens } from "@sunrun/experience-ui-components";
import { useFlags } from "flagsmith/react";
import { ConfirmInfoTaskCard } from "components/molecules/ConfirmInfo/ConfirmInfoTaskCard";
import { SiteModelImage } from "components/organisms/SiteModelImage";
import { SiteModelStatus } from "components/molecules/SiteModelStatus";
import { useDesignRequest } from "hooks/useDesignRequest";
import { useSiteModelInfo } from "hooks/useSiteModelInfo";
import { FeatureFlags } from "fixtures/features";
import { NoImageMap } from "components/molecules/ConfirmInfo/NoImageMap";
import { SiteModelPriorityModal } from "components/molecules/SiteModelPriorityModal";
import {
  isSiteModelComplete,
  getSiteModelMinimumValidDate,
} from "utils/siteModelUtils";

const SiteModelTask = ({
  prospectId,
  openModal,
}: {
  prospectId: string;
  openModal: () => void;
}) => {
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isFailureModalOpen, setIsFailureModalOpen] = useState(false);
  const [isPriorityModalOpen, setIsPriorityModalOpen] = useState(false);
  const [isPriorityRefreshing, setIsPriorityRefreshing] = useState(false);
  const [inQueue, setInQueue] = useState(false);
  const flags = useFlags([FeatureFlags.CONFIRM_INFO_SHOW_DESIGN_REQUEST]);
  const showDesignRequestFlag =
    flags[FeatureFlags.CONFIRM_INFO_SHOW_DESIGN_REQUEST]?.enabled;
  const swrOptions = {
    revalidateOnFocus: inQueue,
    refreshInterval: inQueue ? 8000 : undefined,
  };
  const {
    data: {
      designRequest,
      isDesignRequestRequired = true,
      designRequests,
    } = {},
    mutate,
  } = useDesignRequest(prospectId, swrOptions);
  const siteModelMinimumValidDate =
    getSiteModelMinimumValidDate(designRequests);
  const { data: { siteModelImageUrl, siteModelHasRoofPlanes = false } = {} } =
    useSiteModelInfo(prospectId, swrOptions, siteModelMinimumValidDate);
  const { failureReason, failureDescription, notesToSales } =
    designRequest || {};

  const defaultFailureReason = "Unable to create your site model";
  const defaultFailureDescription =
    "Contact the site modeling team for more information.";

  /**
   * Performs any updates necessary whenever the state of design request
   * changes. (For eg, if we are waiting in the queue)
   */
  useEffect(() => {
    const missingDesignRequest = designRequest === null;
    if (missingDesignRequest || designRequest?.status === "Open") {
      setInQueue(true);
    } else {
      setInQueue(false);
    }
  }, [designRequest]);

  const taskAction = useMemo(() => {
    if (isPriorityRefreshing) {
      return (
        <Button color="secondary" disabled>
          <LoadingIndicator
            color="black"
            height={20}
            width={20}
            showLoadingMessage={false}
          />
        </Button>
      );
    }
    if (
      designRequest?.status === "Open" &&
      designRequest?.priority === "Normal"
    ) {
      return (
        <Button
          onClick={() => setIsPriorityModalOpen(true)}
          color="secondary"
          size="xs"
        >
          Request Priority
        </Button>
      );
    }
    return (
      <Button
        onClick={openModal}
        color="secondary"
        size="xs"
        style={{ height: "fit-content" }}
      >
        Request update
      </Button>
    );
  }, [designRequest, openModal, isPriorityRefreshing]);

  const showSiteModelImage = isSiteModelComplete(
    isDesignRequestRequired,
    designRequest,
    siteModelHasRoofPlanes
  );

  const overrideComplete = !isDesignRequestRequired && siteModelHasRoofPlanes;

  return (
    <ConfirmInfoTaskCard
      taskHeader="4. Site model progress"
      taskAction={taskAction}
      loading={designRequest === undefined}
    >
      <SiteModelStatus
        designRequest={designRequest}
        overrideComplete={overrideComplete}
        useDesignRequest={isDesignRequestRequired || showDesignRequestFlag}
        onClick={openModal}
        onViewDetails={() => setIsFailureModalOpen(true)}
        showCTA={false}
      />
      {showSiteModelImage ? (
        <StyledSiteModelImage
          style={{
            minHeight: "100px",
            minWidth: "50%",
            maxWidth: "50%",
            marginLeft: "auto",
            marginRight: "16px",
            marginTop: "16px",
            borderRadius: "8px",
          }}
          designRequest={designRequest}
          siteModelImageUrl={siteModelImageUrl}
          siteModelHasRoofPlanes={siteModelHasRoofPlanes}
          isDesignRequestRequired={isDesignRequestRequired}
          isMapModalOpen={isMapModalOpen}
          setIsMapModalOpen={setIsMapModalOpen}
          mapStyle={{ borderRadius: "8px" }}
        />
      ) : (
        <NoImageMap />
      )}
      {isMapModalOpen && (
        <StyledModal
          onClose={() => setIsMapModalOpen(false)}
          testId="site-model-task-map-modal"
        >
          <StyledDataContainer>
            <SiteModelImage
              designRequest={designRequest}
              siteModelImageUrl={siteModelImageUrl}
              siteModelHasRoofPlanes={siteModelHasRoofPlanes}
              isDesignRequestRequired={isDesignRequestRequired}
              isMapModalOpen={isMapModalOpen}
              setIsMapModalOpen={setIsMapModalOpen}
            />
            <SiteModelStatus
              designRequest={designRequest}
              overrideComplete={overrideComplete}
              useDesignRequest={
                isDesignRequestRequired || showDesignRequestFlag
              }
              onClick={openModal}
              onViewDetails={() => setIsFailureModalOpen(true)}
            />
          </StyledDataContainer>
        </StyledModal>
      )}
      {isFailureModalOpen && (
        <ErrorModal
          testId="site-model-task-failure-modal"
          title="The Site Modeling Team has Identified an Issue on your model"
          onClose={() => setIsFailureModalOpen(false)}
          error={failureReason || defaultFailureReason}
          primaryButton={{
            text: "New Design Request",
            onClick: () => {
              setIsFailureModalOpen(false);
              openModal();
            },
          }}
          secondaryButton={{
            text: "Go Back",
            onClick: () => {
              setIsFailureModalOpen(false);
            },
          }}
        >
          <FailureModalDescription>
            <FailureDescriptionHead>
              Site modeling team failure reason
            </FailureDescriptionHead>
            <FailurDescriptionBody>
              {failureReason || defaultFailureReason}
            </FailurDescriptionBody>
          </FailureModalDescription>
          {(failureDescription || !notesToSales) && (
            <FailureModalDescription>
              <FailureDescriptionHead>
                Site modeling team failure description
              </FailureDescriptionHead>
              <FailurDescriptionBody>
                {failureDescription || defaultFailureDescription}
              </FailurDescriptionBody>
            </FailureModalDescription>
          )}
          {notesToSales && (
            <FailureModalDescription>
              <FailureDescriptionHead>
                Notes to Sales Team from Designer
              </FailureDescriptionHead>
              <FailurDescriptionBody>{notesToSales}</FailurDescriptionBody>
            </FailureModalDescription>
          )}
        </ErrorModal>
      )}
      {isPriorityModalOpen && (
        <SiteModelPriorityModal
          onClose={() => setIsPriorityModalOpen(false)}
          refresh={async () => {
            setIsPriorityRefreshing(true);
            await mutate();
            setIsPriorityRefreshing(false);
          }}
          status={
            <SiteModelStatus
              designRequest={designRequest}
              useDesignRequest={
                isDesignRequestRequired || showDesignRequestFlag
              }
            />
          }
          prospectId={prospectId}
          designRequestId={designRequest?.DesignRequestID || ""}
        />
      )}
    </ConfirmInfoTaskCard>
  );
};

const FailureModalDescription = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  padding-bottom: 20px;
`;

const FailureDescriptionHead = styled.div`
  color: ${tokens.TINTS_OFF_WHITE_20};
  font-size: 14px;
`;

const FailurDescriptionBody = styled.div`
  color: ${tokens.BRAND_HERO_BLUE};
`;

const StyledSiteModelImage = styled(SiteModelImage)`
  max-height: 400px;
`;

const StyledModal = styled(Modal)`
  .modal-container {
    width: 820px;
    max-width: 90%;
    margin: auto;
  }
  .sr-button-secondary {
    width: 268px;
  }
`;

const StyledDataContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export { SiteModelTask };
